import {
  createContext,
  FC,
  RefObject,
  useContext,
  useMemo,
  useState,
} from 'react';
import { AuthContext, CommonContext } from '@repo-level/core-front';

import { abTestingFunction } from 'helpers/abTesting';
import { AddonsContext } from 'contexts/addons';
import { CommonUtilsContext } from 'contexts/commonUtils';

interface TabBarData {
  tabBarHeight: number;
  handleTabBarHeight: (ref: RefObject<HTMLDivElement>) => void;
  isTournamentsInTabBar: boolean;
}

enum TournamentsInTabBarABTest {
  A_DEFAULT = 'a_exp_add_tournaments_on_tabbar',
  B_WITH_TOURNAMENTS = 'b_exp_add_tournaments_on_tabbar',
}

export const TabBarContext = createContext<TabBarData>(null as any);

export const TabBarContextProvider: FC = ({ children }) => {
  const [tabBarHeight, setTabBarHeight] = useState(0);
  const { currentIp } = useContext(CommonContext);
  const { user, isAuthenticated } = useContext(AuthContext);
  const { cmsABTests } = useContext(AddonsContext);
  const { isMobile } = useContext(CommonUtilsContext);

  const handleTabBarHeight = (ref: RefObject<HTMLDivElement>) => {
    if (ref === null) return;
    ref.current?.clientHeight
      ? setTabBarHeight(ref.current?.clientHeight)
      : setTabBarHeight(0);
  };

  const isTournamentsInTabBar = useMemo(() => {
    if (!isAuthenticated || !isMobile) return false;
    const tournamentsInTabBarABTestOption = abTestingFunction(
      'exp_add_tournaments_on_tabbar',
      {
        defaultVersion: TournamentsInTabBarABTest.A_DEFAULT,
        versions: [
          {
            name: TournamentsInTabBarABTest.A_DEFAULT,
            probability: 50,
          },
          {
            name: TournamentsInTabBarABTest.B_WITH_TOURNAMENTS,
            probability: 50,
          },
        ],
      },
      user,
      currentIp,
      cmsABTests
    );

    return (
      tournamentsInTabBarABTestOption ===
      TournamentsInTabBarABTest.B_WITH_TOURNAMENTS
    );
  }, [currentIp, user, cmsABTests, isAuthenticated, isMobile]);

  return (
    <TabBarContext.Provider
      value={{
        tabBarHeight,
        handleTabBarHeight,
        isTournamentsInTabBar,
      }}
    >
      {children}
    </TabBarContext.Provider>
  );
};
